import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../lib/money';
import { Badge, Button, ConfirmDialog, DataTable, Drawer, EmptyState, Spinner, useToast } from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { catalogApi } from './catalogApi';
import SupplierItemForm from './SupplierItemForm';

export default function ItemSuppliersTab({ itemId, suppliers = [], units = [], canManage, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || id;
  const unitCode = (id) => units.find((u) => u.id === id)?.code;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogApi.listItemSuppliers(itemId);
      setLinks(res || []);
    } catch (err) {
      toast.error(t('Could not load suppliers'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [itemId, toast, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (l) => { setEditing(l); setDrawerOpen(true); };

  const save = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) await catalogApi.updateItemSupplier(itemId, editing.id, payload);
      else await catalogApi.addItemSupplier(itemId, payload);
      toast.success(editing ? t('Supplier updated') : t('Supplier added'));
      setDrawerOpen(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not save'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removeItemSupplier(itemId, deleteTarget.id);
      toast.success(t('Supplier removed'));
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not remove'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'supplier',
      header: t('Supplier'),
      width: 240,
      render: (row) => (
        <div style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {supplierName(row.supplierId)}
          {row.isPreferred && <Badge tone="brand">{t('Preferred')}</Badge>}
          {!row.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
        </div>
      ),
    },
    { key: 'sku', header: t('Supplier SKU'), width: 150, render: (row) => row.supplierSku || <span className="pp-cell-muted">—</span> },
    {
      key: 'price',
      header: t('Price'),
      width: 130,
      align: 'right',
      render: (row) => (row.price ? money(row.price) : <span className="pp-cell-muted">—</span>),
    },
    {
      key: 'pack',
      header: t('Pack'),
      width: 120,
      render: (row) => `${row.packSize}${unitCode(row.purchaseUomId) ? ` ${unitCode(row.purchaseUomId)}` : ''}`,
    },
    { key: 'moq', header: t('MOQ'), width: 90, align: 'right', render: (row) => (row.minimumOrderQuantity ?? <span className="pp-cell-muted">—</span>) },
    { key: 'lead', header: t('Lead'), width: 90, align: 'right', render: (row) => (row.leadTimeDays != null ? `${row.leadTimeDays}d` : <span className="pp-cell-muted">—</span>) },
    {
      key: 'actions',
      header: '',
      width: 90,
      align: 'right',
      resizable: false,
      render: (row) => canManage ? (
        <div className="pp-row-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton aria-label={t('Edit')} variant="ghost" size="icon-sm" onClick={() => openEdit(row)}><Icon.Pencil size={15} /></IconButton>
          <IconButton aria-label={t('Remove')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(row)}><Icon.Trash size={15} /></IconButton>
        </div>
      ) : null,
    },
  ];

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  return (
    <>
      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('Add supplier')}</Button>
        )}
      </div>

      {links.length === 0 ? (
        <EmptyState
          icon={<Icon.Building size={28} />}
          title={t('No suppliers linked')}
          description={t('Link suppliers and their prices so this item can be purchased.')}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('Add supplier')}</Button> : null}
        />
      ) : (
        <DataTable columns={columns} data={links} rowKey={(r) => r.id} density="comfortable" />
      )}

      <Drawer open={drawerOpen} onClose={() => (submitting ? null : setDrawerOpen(false))} title={editing ? t('Edit supplier price') : t('Add supplier')} size="md">
        <SupplierItemForm link={editing} suppliers={suppliers} units={units} submitting={submitting} onSubmit={save} onCancel={() => setDrawerOpen(false)} />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Remove supplier')}
        description={deleteTarget ? t("Remove {name} from this item's price list?", { name: supplierName(deleteTarget.supplierId) }) : ''}
        confirmLabel={t('Remove')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
